import { useState, useEffect, useCallback } from "react";
import { API, axios } from "@/api";
import { toast } from "sonner";
import { Loader2, CheckCircle, XCircle, Clock } from "lucide-react";

export default function BrandContributionsPanel() {
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [actioning, setActioning] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/brand-contributions?status=${filter}`);
      setContributions(res.data.contributions || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleAction = async (id, action) => {
    setActioning(id);
    try {
      await axios.post(`${API}/admin/brand-contributions/${id}/${action}`);
      toast.success(`Contribution ${action}d`);
      fetch();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to ${action}`);
    } finally { setActioning(null); }
  };

  return (
    <div data-testid="brand-contributions-panel">
      {/* Filter tabs */}
      <div className="flex gap-1 mb-4" data-testid="brand-contrib-filters">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition ${
              filter === s ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
            data-testid={`brand-contrib-filter-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-slate-300" />
        </div>
      ) : contributions.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-6">No {filter} contributions</p>
      ) : (
        <div className="space-y-3">
          {contributions.map((c) => (
            <div
              key={c.contribution_id}
              className="bg-slate-50 rounded-xl border border-slate-100 p-3"
              data-testid={`brand-contrib-${c.contribution_id}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm font-bold text-slate-800">{c.business_name}</p>
                  <p className="text-[10px] text-slate-400">
                    {c.contribution_id} · {new Date(c.submitted_at).toLocaleDateString()}
                  </p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                  c.status === "pending" ? "bg-amber-100 text-amber-600" :
                  c.status === "approved" ? "bg-emerald-100 text-emerald-600" :
                  "bg-red-100 text-red-600"
                }`}>
                  {c.status === "pending" && <Clock className="w-3 h-3 inline mr-0.5" />}
                  {c.status}
                </span>
              </div>

              {/* Submission data */}
              <div className="text-xs text-slate-600 space-y-1">
                {c.submission_data?.description && (
                  <p><span className="font-medium text-slate-500">Description:</span> {c.submission_data.description}</p>
                )}
                {c.submission_data?.year_founded && (
                  <p><span className="font-medium text-slate-500">Founded:</span> {c.submission_data.year_founded}</p>
                )}
                {c.submission_data?.website && (
                  <p><span className="font-medium text-slate-500">Website:</span> {c.submission_data.website}</p>
                )}
                {(c.submission_data?.instagram || c.submission_data?.twitter || c.submission_data?.facebook) && (
                  <p className="text-slate-400">
                    {c.submission_data.instagram && `IG: ${c.submission_data.instagram} `}
                    {c.submission_data.twitter && `X: ${c.submission_data.twitter} `}
                    {c.submission_data.facebook && `FB: ${c.submission_data.facebook}`}
                  </p>
                )}
              </div>

              {/* Actions for pending */}
              {c.status === "pending" && (
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => handleAction(c.contribution_id, "approve")}
                    disabled={actioning === c.contribution_id}
                    className="flex-1 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-50"
                    data-testid={`approve-${c.contribution_id}`}
                  >
                    {actioning === c.contribution_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                    Approve
                  </button>
                  <button
                    onClick={() => handleAction(c.contribution_id, "reject")}
                    disabled={actioning === c.contribution_id}
                    className="flex-1 py-1.5 rounded-lg bg-red-500 text-white text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-50"
                    data-testid={`reject-${c.contribution_id}`}
                  >
                    {actioning === c.contribution_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                    Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
